import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { useGetLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { css } from "styled-components";

const enum fields {
    links = "links",
    customCSS = "customCSS",
}

export interface LinkModel {
    fields: {
        destination: LinkFieldValue;
        overriddenTitle: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.customCSS]: string;
    };
}

interface TabComponentStyles {
    wrapper?: InjectableCss;
    tabs?: InjectableCss;
    tabButton?: ButtonPresentationProps;
    tabContent?: InjectableCss;
}

export const tabComponentStyles: TabComponentStyles = {
    wrapper: {
        css: css`
            margin-top: 50px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        margin-top: 30px;
                    `,
                    null,
                    null,
                    null,
                ],
                "max",
            )}
        `
    },
    tabs: {
        css: css`
            display: flex;
            gap: 20px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    css`
                        gap: 7px;
                    `,
                    null,
                    null,
                    null,
                ],
                "max",
            )}
        `,
    },
    tabButton: {
        css: css`
            border-radius: 2px;
            border: 0.5px solid #d9d9d9;
            background-color: lightgray;
            cursor: pointer;
            color: #5b5b5b;
            padding: 0 47px;
            min-height: 45px;
            >span{
                font-size: 18px;
                font-weight: 400;
            }
            :hover{
                background-color: #707c92;
                color: #fff;
                border:none;
            }
            
            ${({ theme }) => {
                return breakpointMediaQueries(theme, [
                    css`
                        text-align: left;
                    `,
                    css`
                        text-align: left;
                    `,
                    null,
                    null,
                    null,
                ]);
            }}
            &.active {
                border: 0.5px solid ${getColor("primary")};
                background-color: ${getColor("primary")};
                color: #fff;
            }
        `,
    },
    tabContent: {
        css: css`
            padding: 20px;
            border: 1px solid #ccc;
            margin-bottom: 20px;
        `,
    },
};

const styles = tabComponentStyles;

type Props = OwnProps;

const TabComponent: React.FC<Props> = ({ id, fields }) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const [activeTab, setActiveTab] = useState(0);

    const handleTabClick = (index: number) => {
        setActiveTab(index);
    };

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper className="tabs" {...styles.tabs}>
                {links.map((link, index) => (
                    <Button
                        {...styles.tabButton}
                        key={index}
                        className={`tab-button ${activeTab === index ? "active" : ""}`}
                        onClick={() => handleTabClick(index)}
                    >
                        {fields.links[index].fields.overriddenTitle}
                    </Button>
                ))}
            </StyledWrapper>
            <StyledWrapper className="tab-content" {...styles.tabContent}>
                <Zone contentId={id} zoneName={fields.links[activeTab]?.fields.overriddenTitle} />
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const widgetModule: WidgetModule = {
    component: TabComponent,
    definition: {
        group: "Brand Details",
        displayName: "Category Tabs",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: item => {
                    const {
                        destination: { value },
                        overriddenTitle,
                    } = item.fields;
                    return overriddenTitle || value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: { value: "", type: "Page" },
                        isVisible: () => false,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
                tab: basicTab,
            },
        ],
    },
};

export default widgetModule;
